// src/firebase.js
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyAuNJsIRqJeMNzLTakEjoqTR3ADbV43Bf8",
  authDomain: "garagemitra-cc02f.firebaseapp.com",
  projectId: "garagemitra-cc02f",
  storageBucket: "garagemitra-cc02f.firebasestorage.app",
  messagingSenderId: "980262574400",
  appId: "1:980262574400:web:611e3c3e2820d9e3d402a5",
  measurementId: "G-LVF6DLE3TM",
};

const VAPID_KEY = "BIlAoEaQDPGUJEa2LhRzaYI1j1cZQFK2b3naoR9uK_ft0zFn4yE5eb7pKbMMmh7x1tawAMGHWkiKKdirDDa7HBY";

let app;
let messaging;

try {
  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized');
} catch (error) {
  console.error('Firebase app initialization error:', error);
}

if (app) {
  try {
    messaging = getMessaging(app);
    console.log('Firebase messaging initialized');
  } catch (error) {
    console.error('Firebase messaging initialization error:', error);
  }
}

// Wait for service worker to be active
const waitForServiceWorkerActive = async (registration) => {
  return new Promise((resolve, reject) => {
    if (registration.active) {
      resolve(registration);
      return;
    }
    
    const timeout = setTimeout(() => {
      console.warn('Service worker activation timeout');
      resolve(registration); // Resolve anyway to continue
    }, 5000);

    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;
      if (installingWorker) {
        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'activated') {
            clearTimeout(timeout);
            console.log('Service worker activated');
            resolve(registration);
          }
        });
      }
    });

    // Also check existing state
    if (registration.waiting) {
      registration.waiting.addEventListener('statechange', () => {
        if (registration.waiting.state === 'activated') {
          clearTimeout(timeout);
          console.log('Service worker activated from waiting');
          resolve(registration);
        }
      });
    }
  });
};

// Register service worker with proper activation
export const registerServiceWorker = async () => {
  try {
    // Check if we're in a secure context
    if (!window.isSecureContext && window.location.hostname !== 'localhost') {
      console.warn('FCM requires HTTPS or localhost');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    let registration;
    const server_folder = localStorage.getItem("server_folder") || "";
    
    // IMPORTANT: Correct path for your live URL
    const swPath = `${server_folder}/firebase-messaging-sw.js`;
    const swScope = `${server_folder}/`;
    
    console.log('Registering service worker at:', swPath, 'with scope:', swScope);
    
    // First try to get existing registration
    try {
      registration = await navigator.serviceWorker.getRegistration(swScope);
      if (registration) {
        console.log('Found existing service worker registration:', registration.scope);
      }
    } catch (error) {
      console.log('No existing service worker found');
    }
    
    // If no existing registration, register new one
    if (!registration) {
      try {
        registration = await navigator.serviceWorker.register(swPath, {
          scope: swScope
        });
        console.log('New service worker registered:', registration.scope);
      } catch (registerError) {
        console.error('Failed to register service worker:', registerError);
        return null;
      }
    }
    
    // Wait for service worker to be active
    registration = await waitForServiceWorkerActive(registration);
    
    if (!registration.active) {
      console.warn('Service worker is not active, attempting to activate...');
      // Try to update/activate
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      // Wait a bit more
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (registration.active) {
      console.log('Service worker is active and ready');
      return registration;
    } else {
      console.warn('Service worker is not active');
      return null;
    }
  } catch (error) {
    console.error('Error in registerServiceWorker:', error);
    return null;
  }
};

// Check if FCM is supported
export const isFCMSupported = async () => {
  try {
    return await isSupported();
  } catch (error) {
    console.error('Error checking FCM support:', error);
    return false;
  }
};

// Request permission and get token with detailed logging
export const requestForToken = async (garageId, companyId) => {
  // console.log('=== FCM TOKEN GENERATION STARTED ===');
  // console.log('1. Checking prerequisites...');
  
  try {
    if (!messaging) {
      console.error('❌ Firebase messaging not initialized');
      return null;
    }

    const fcmSupported = await isFCMSupported();
    // console.log(`2. FCM supported: ${fcmSupported}`);
    
    if (!fcmSupported) {
      console.error('❌ FCM not supported in this browser');
      return null;
    }

    // Check notification permission
    console.log('3. Checking notification permission...');
    if (!('Notification' in window)) {
      console.error('❌ Notifications not supported');
      return null;
    }

    let permission = Notification.permission;
    // console.log(`4. Current permission: ${permission}`);
    
    if (permission === 'default') {
      // console.log('5. Requesting permission...');
      permission = await Notification.requestPermission();
      // console.log(`6. New permission: ${permission}`);
    }

    if (permission !== 'granted') {
      console.error(`❌ Notification permission not granted: ${permission}`);
      return null;
    }

    // console.log('✅ Notification permission granted');

    // Register service worker
    // console.log('7. Registering service worker...');
    const registration = await registerServiceWorker();
    
    if (!registration) {
      console.error('❌ Service Worker registration failed');
      return null;
    }

    if (!registration.active) {
      console.error('❌ Service Worker is not active');
      return null;
    }

    // console.log('✅ Service Worker is active');
    // console.log('8. Getting FCM token...');

    // Get FCM token
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (currentToken) {
      // console.log('✅ FCM Token obtained successfully');
      // console.log('Token (first 20 chars):', currentToken.substring(0, 20) + '...');
      // console.log('Token length:', currentToken.length);
      
      // Store token locally
      localStorage.setItem('fcm_token', currentToken);
      localStorage.setItem('fcm_token_time', Date.now().toString());
      
      // console.log('9. Sending token to server...');
      // Send to server
      await sendTokenToServer(currentToken, garageId, companyId);
      
      // console.log('=== FCM TOKEN GENERATION COMPLETE ===');
      return currentToken;
    } else {
      console.log('❌ No registration token available');
      
      // Try to get token from existing subscription
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // console.log('Found existing push subscription');
        const tokenDetails = subscription.toJSON();
        // console.log('Subscription endpoint:', tokenDetails.endpoint);
      }
      
      return null;
    }
  } catch (error) {
    console.error('❌ An error occurred while retrieving token:', error);
    console.error('Error details:', error.message);
    console.error('Error code:', error.code);
    console.error('Error name:', error.name);
    
    // More detailed error analysis
    if (error.code === 'messaging/unsupported-browser') {
      console.error('Browser does not support FCM');
    } else if (error.code === 'messaging/permission-blocked') {
      console.error('Notifications blocked by browser');
    } else if (error.code === 'messaging/permission-default') {
      console.error('Notifications permission not granted');
    } else if (error.code === 'messaging/failed-service-worker-registration') {
      console.error('Service worker registration failed');
    }
    
    return null;
  }
};

// Send token to server
const sendTokenToServer = async (token, garageId, companyId) => {
  try {
    const server_url = localStorage.getItem("server_url");
    
    if (!server_url) {
      console.error('❌ Server URL not found in localStorage');
      return;
    }

    // console.log('Sending token to:', `${server_url}store_token.php`);
    
    const formData = new FormData();
    formData.append("garage_id", garageId);
    formData.append("company_id", companyId);
    formData.append("web_token", token);
    formData.append("platform", "web");

    const response = await fetch(`${server_url}store_token.php`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('Server response:', result);
    
    if (result.status === '1') {
      // console.log('✅ Token stored successfully on server');
    } else {
      console.error('❌ Failed to store token on server:', result.error);
    }
  } catch (error) {
    console.error('❌ Error storing token on server:', error);
  }
};

// Listen for foreground messages
export const onMessageListener = () => {
  return new Promise((resolve, reject) => {
    if (!messaging) {
      reject(new Error('Messaging not initialized'));
      return;
    }

    onMessage(messaging, (payload) => {
      // console.log("📨 Received foreground message:", payload);
      resolve(payload);
    });
  });
};

// Debug function to check service worker status
export const debugServiceWorker = async () => {
  const logs = [];
  
  try {
    logs.push('=== Service Worker Debug ===');
    
    // Check secure context
    logs.push(`Secure context: ${window.isSecureContext}`);
    logs.push(`Hostname: ${window.location.hostname}`);
    
    // Check service worker support
    logs.push(`Service Worker supported: ${'serviceWorker' in navigator}`);
    
    if ('serviceWorker' in navigator) {
      const server_folder = localStorage.getItem("server_folder") || "";
      const swScope = `${server_folder}/`;
      
      // Check registrations
      const registrations = await navigator.serviceWorker.getRegistrations();
      logs.push(`Total registrations: ${registrations.length}`);
      
      registrations.forEach((reg, index) => {
        logs.push(`Registration ${index + 1}:`);
        logs.push(`  Scope: ${reg.scope}`);
        logs.push(`  Active: ${!!reg.active}`);
        logs.push(`  Waiting: ${!!reg.waiting}`);
        logs.push(`  Installing: ${!!reg.installing}`);
        if (reg.active) {
          logs.push(`  Active state: ${reg.active.state}`);
        }
      });
      
      // Check specific registration
      const registration = await navigator.serviceWorker.getRegistration(swScope);
      if (registration) {
        logs.push(`Found registration for scope ${swScope}`);
        logs.push(`Active worker: ${!!registration.active}`);
        
        if (registration.active) {
          // Check push subscription
          const subscription = await registration.pushManager.getSubscription();
          logs.push(`Has push subscription: ${!!subscription}`);
          if (subscription) {
            const subJson = subscription.toJSON();
            logs.push(`Subscription endpoint: ${subJson.endpoint?.substring(0, 50)}...`);
          }
        }
      } else {
        logs.push(`No registration found for scope ${swScope}`);
      }
    }
    
  } catch (error) {
    logs.push(`Error in debug: ${error.message}`);
  }
  
  return logs;
};

export { messaging };
export default app;